import { createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { UnauthorizedError, apiGet } from "@/lib/api.ts";
import { setActiveOrgId } from "@/lib/activeOrg.ts";
import { AuthApiError, type OrgInvitation, acceptInvitation, getInvitation } from "@/lib/auth.ts";

// FRE-279: invitation accept page.
//
// `$token` is Better Auth's invitation `id` (the row is the token in the
// current schema). The page requires login — beforeLoad redirects to /login
// with `next=` so the user lands back here after signing in.

type Me = { userId: string };

export const Route = createFileRoute("/invitations/$token")({
  beforeLoad: async ({ params }) => {
    try {
      await apiGet<Me>("/api/v1/me");
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({
          to: "/login",
          search: { next: `/invitations/${params.token}` },
        });
      }
      throw err;
    }
  },
  component: AcceptInvitationComponent,
});

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; invitation: OrgInvitation & { organizationName?: string } }
  | { kind: "error"; message: string };

function AcceptInvitationComponent() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const router = useRouter();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const inv = await getInvitation(token);
        if (cancelled) return;
        setState({ kind: "ready", invitation: inv });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Invitation not found";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept() {
    if (state.kind !== "ready") return;
    setBusy(true);
    setActionError(null);
    try {
      await acceptInvitation(token);
      // Switch active org to the one we just joined so the home view loads
      // its files immediately. The server-side session row is also written
      // by `setActiveOrgId` for durability.
      await setActiveOrgId(state.invitation.organizationId);
      await router.invalidate();
      await navigate({ to: "/" });
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : "Failed to accept invitation";
      setActionError(msg);
    } finally {
      setBusy(false);
    }
  }

  function handleDecline() {
    // Decline is "leave the page". Better Auth has a reject endpoint but the
    // FRE-279 acceptance criteria only requires accept; declining is just a
    // navigation away. Skip the API call to avoid surfacing extra failure
    // modes for an already-soft action.
    void navigate({ to: "/" });
  }

  return (
    <div className="max-w-md mx-auto mt-12 px-6">
      <h1 className="text-2xl font-semibold mb-6">Accept invitation</h1>

      {state.kind === "loading" ? (
        <p className="text-sm text-muted-foreground">Loading invitation…</p>
      ) : null}

      {state.kind === "error" ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {state.message}
        </div>
      ) : null}

      {state.kind === "ready" ? (
        <div className="rounded-md border border-border bg-card p-5">
          <p className="text-sm text-foreground/80">
            You've been invited to join{" "}
            <span className="font-semibold text-foreground">
              {state.invitation.organizationName ?? "an organization"}
            </span>{" "}
            as{" "}
            <span className="font-semibold text-foreground">
              {state.invitation.role ?? "member"}
            </span>
            .
          </p>
          {actionError ? <p className="mt-3 text-sm text-destructive">{actionError}</p> : null}
          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleAccept()}
              disabled={busy}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? "Accepting…" : "Accept invitation"}
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={busy}
              className="rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-foreground/80 hover:bg-muted disabled:opacity-50"
            >
              Decline
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
